import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { SectionWrapper, SectionHeader, DOCUMENT_COLORS } from './documentStyles';

/**
 * Legal clauses data — Zimbabwe jurisdiction
 * Mirrors backend/src/constants/legalClauses.js
 * Version: 1.0.0
 */
const CLAUSES_VERSION = '1.0.0';

const SHARED_CLAUSES = {
  governingLaw: {
    title: 'Governing Law & Jurisdiction',
    text: 'This agreement shall be governed by and construed in accordance with the laws of Zimbabwe. Any dispute arising out of or in connection with this agreement shall be subject to the exclusive jurisdiction of the courts of Zimbabwe.',
  },
  disputeResolution: {
    title: 'Dispute Resolution',
    text: 'In the event of any dispute, the parties shall first attempt to resolve the matter through good-faith negotiation within fourteen (14) calendar days. If negotiation fails, the dispute shall be submitted to mediation. If mediation fails within thirty (30) calendar days, the matter shall be referred to binding arbitration in accordance with the Arbitration Act [Chapter 7:15] of Zimbabwe.',
  },
  forceMajeure: {
    title: 'Force Majeure',
    text: 'Neither party shall be liable for failure or delay resulting from circumstances beyond reasonable control, including natural disasters, epidemic, war, government restrictions, or power failures. If the force majeure event continues for more than sixty (60) calendar days, either party may terminate with fourteen (14) days\' written notice.',
  },
  confidentiality: {
    title: 'Confidentiality',
    text: 'Both parties agree to keep confidential all information received from the other party that is not publicly available. This obligation survives termination for a period of two (2) years.',
  },
  limitationOfLiability: {
    title: 'Limitation of Liability',
    text: 'The total aggregate liability of either party shall not exceed the total contract value. Neither party shall be liable for indirect, incidental, special, consequential, or punitive damages. Nothing limits liability for death, personal injury, or fraud.',
  },
  indemnification: {
    title: 'Indemnification',
    text: 'Each party shall indemnify and hold harmless the other against all claims, losses, and expenses arising from breach of this agreement or negligent acts by that party or its agents.',
  },
  termination: {
    title: 'Termination',
    text: 'Either party may terminate with fourteen (14) calendar days\' written notice. Immediate termination is available for material breach not remedied within fourteen (14) days, or insolvency. Upon termination, all outstanding payments for completed work become due within thirty (30) days.',
  },
};

const BID_CLAUSES = {
  variation: {
    title: 'Variations & Change Orders',
    text: 'No variation to scope, specifications, timeline, or price shall be effective unless agreed in writing through a formal Change Order on the Eqaya platform. Work performed without an approved Change Order is at the Contractor\'s risk.',
  },
  defectsLiability: {
    title: 'Defects Liability Period',
    text: 'The Contractor shall be liable for defects apparent within twelve (12) months from completion. Defects due to defective materials or poor workmanship shall be rectified at the Contractor\'s cost within a reasonable time.',
  },
  warranty: {
    title: 'Warranty',
    text: 'The Contractor warrants that: (a) all work shall be carried out in a professional manner; (b) all materials shall be new and fit for purpose; (c) completed work shall comply with all applicable laws, building codes, and standards in Zimbabwe; (d) work shall be free from defects for twelve (12) months from completion.',
  },
};

const MILESTONE_CLAUSES = {
  milestoneAcceptance: {
    title: 'Milestone Acceptance & Payment Release',
    text: 'The Client shall approve, request changes, or reject the milestone within seven (7) business days. Payment releases from escrow upon approval. Failure to respond within seven (7) business days results in automatic approval. Disputes may be referred to an independent verifier on the Eqaya platform.',
  },
  retention: {
    title: 'Retention',
    text: 'The Client may retain 5% of each milestone payment as security for performance. Retention is released upon expiry of the Defects Liability Period, provided all defects have been rectified.',
  },
};

const VERIFICATION_CLAUSES = {
  verifierIndependence: {
    title: 'Verifier Independence & Liability',
    text: 'The Verifier confirms no financial interest in or relationship with either the Client or the Contractor. The report represents a professional opinion based on conditions observed at the time of inspection. The Verifier does not guarantee all defects have been identified.',
  },
};

/**
 * Get clauses for a specific document type
 */
function getClausesForType(documentType) {
  switch (documentType) {
    case 'bid':
      return [
        SHARED_CLAUSES.governingLaw,
        BID_CLAUSES.variation,
        BID_CLAUSES.warranty,
        BID_CLAUSES.defectsLiability,
        SHARED_CLAUSES.termination,
        SHARED_CLAUSES.confidentiality,
        SHARED_CLAUSES.limitationOfLiability,
        SHARED_CLAUSES.indemnification,
        SHARED_CLAUSES.forceMajeure,
        SHARED_CLAUSES.disputeResolution,
      ];
    case 'milestone_report':
      return [
        MILESTONE_CLAUSES.milestoneAcceptance,
        BID_CLAUSES.variation,
        MILESTONE_CLAUSES.retention,
        BID_CLAUSES.defectsLiability,
        SHARED_CLAUSES.disputeResolution,
      ];
    case 'verification_report':
      return [
        VERIFICATION_CLAUSES.verifierIndependence,
        SHARED_CLAUSES.governingLaw,
        SHARED_CLAUSES.limitationOfLiability,
        SHARED_CLAUSES.disputeResolution,
      ];
    case 'contractor_hire':
    case 'verifier_hire':
      return [
        SHARED_CLAUSES.governingLaw,
        SHARED_CLAUSES.confidentiality,
        SHARED_CLAUSES.termination,
        SHARED_CLAUSES.disputeResolution,
      ];
    default:
      return [
        SHARED_CLAUSES.governingLaw,
        SHARED_CLAUSES.disputeResolution,
      ];
  }
}

/**
 * LegalClausesSection — Renders legal clauses as expandable accordions
 *
 * Props:
 *  documentType — 'bid' | 'milestone_report' | 'contractor_hire' | 'verifier_hire' | 'verification_report'
 *  sectionNumber — Optional section number prefix
 *  additionalTerms — Optional additional terms text (user-provided, shown above clauses)
 */
const LegalClausesSection = ({ documentType, sectionNumber, additionalTerms }) => {
  const clauses = getClausesForType(documentType);
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <SectionWrapper>
      <SectionHeader
        icon={<GavelOutlinedIcon fontSize="small" />}
        title={sectionNumber ? `${sectionNumber}. Legal Terms & Conditions` : 'Legal Terms & Conditions'}
      />

      {/* Jurisdiction banner */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          bgcolor: DOCUMENT_COLORS.brandLight,
          borderColor: DOCUMENT_COLORS.brand,
          borderLeft: `4px solid ${DOCUMENT_COLORS.brand}`,
        }}
      >
        <Typography variant="body2" fontWeight="bold" color={DOCUMENT_COLORS.brandDark} gutterBottom>
          Governing Jurisdiction: Republic of Zimbabwe
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This agreement is governed by the Electronic Transactions Act [Chapter 22:05] and the laws of Zimbabwe.
          All parties submit to the exclusive jurisdiction of the Zimbabwean courts.
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Chip
            label={'Clauses v' + CLAUSES_VERSION}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20, borderColor: DOCUMENT_COLORS.brand, color: DOCUMENT_COLORS.brand }}
          />
        </Box>
      </Paper>

      {/* Additional user-provided terms */}
      {additionalTerms && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Additional Terms
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {additionalTerms}
          </Typography>
        </Box>
      )}

      {/* Legal clauses as expandable accordions */}
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
        Standard Legal Clauses ({clauses.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Click to expand each clause. By signing this document, all parties agree to these terms.
      </Typography>

      {clauses.map((clause, index) => (
        <Accordion
          key={index}
          expanded={expanded === index}
          onChange={handleAccordionChange(index)}
          variant="outlined"
          disableGutters
          sx={{
            mb: 0.5,
            '&:before': { display: 'none' },
            borderColor: DOCUMENT_COLORS.border,
            '&.Mui-expanded': { borderColor: DOCUMENT_COLORS.brand },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 44,
              '& .MuiAccordionSummary-content': { my: 0.5 },
              bgcolor: expanded === index ? DOCUMENT_COLORS.brandLight : 'transparent',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ color: DOCUMENT_COLORS.titleText }}>
              {index + 1}. {clause.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
              {clause.text}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Legal footer */}
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${DOCUMENT_COLORS.border}` }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          These terms are legally binding under the Electronic Transactions Act [Chapter 22:05] of Zimbabwe.
          Electronic signatures on this document carry the same legal weight as handwritten signatures.
          Eqaya (Private) Limited | info@eqaya.com
        </Typography>
      </Box>
    </SectionWrapper>
  );
};

export default LegalClausesSection;
export { getClausesForType, CLAUSES_VERSION };
